import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17611 — every person Workd has paid Apollo to reveal, keyed by Apollo's
 * person id, so nobody is ever paid for twice: ApolloClientService checks
 * this table before any reveal and answers a repeat from here. It also holds
 * the miss: a revealed person who turned out to be in Workd already
 * (`inWorkd`), which the settings page reports as the monthly miss rate.
 *
 * The email is kept only when Apollo marked it verified; an unverified one is
 * never stored or used.
 *
 * Not audited (`auditable: false`): written once by the reveal itself, and
 * the matching `apollo_spend` row is the record of who paid for it.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('apollo_reveal', {}, { auditable: false })
@Index('UK_apollo_reveal_person', ['apolloPersonId'], { unique: true })
@Index('IDX_apollo_reveal_period', ['periodKey'])
export class ApolloRevealEntity extends BaseEntity {
	@Column('varchar', { name: 'apolloPersonId', length: 64 })
	public apolloPersonId!: string;

	@Column('datetime', { name: 'revealedAt' })
	public revealedAt!: Date;

	/** `YYYY-MM` of the reveal in the tenant's zone, for the monthly miss rate. */
	@Column('char', { name: 'periodKey', length: 7 })
	public periodKey!: string;

	/** Tenant `user._id` of the person who paid for it; null for Workd's own jobs. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	@Column('bigint', { name: 'fk_apollo_spend__id', unsigned: true, nullable: true })
	public fkApolloSpendId!: string | null;

	/** Verified emails only. */
	@Column('varchar', { name: 'email', length: 320, nullable: true })
	public email!: string | null;

	/** Apollo's email status as revealed (verified, unverified, likely to engage, ...). */
	@Column('varchar', { name: 'emailStatus', length: 32, nullable: true })
	public emailStatus!: string | null;

	@Column('varchar', { name: 'firstName', length: 100, nullable: true })
	public firstName!: string | null;

	@Column('varchar', { name: 'lastName', length: 100, nullable: true })
	public lastName!: string | null;

	@Column('varchar', { name: 'title', length: 255, nullable: true })
	public title!: string | null;

	@Column('varchar', { name: 'linkedinUrl', length: 500, nullable: true })
	public linkedinUrl!: string | null;

	@Column('varchar', { name: 'phone', length: 64, nullable: true })
	public phone!: string | null;

	@Column('varchar', { name: 'apolloOrgId', length: 64, nullable: true })
	public apolloOrgId!: string | null;

	@Column('varchar', { name: 'organizationName', length: 255, nullable: true })
	public organizationName!: string | null;

	@Column('varchar', { name: 'organizationDomain', length: 253, nullable: true })
	public organizationDomain!: string | null;

	/** The miss: the person was already in Workd when revealed. */
	@Column({ type: 'boolean', name: 'inWorkd', width: 1, default: () => "'0'" })
	public inWorkd!: boolean;

	/** The Workd person the reveal matched, when it matched one. */
	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public fkContactId!: number | null;
}
